import { useState } from 'react';
import { ApiError } from '../../api/client';
import { useInvitations, useRevokeInvitation } from '../../api/hooks';
import type { Invitation, InviteStatus } from '../../types';
import { darkTheme, FONT } from '../../theme/tokens';
import { Spinner } from '../ui/Spinner';
import { ROLE_LABELS } from './roleLabels';

const t = darkTheme;
const PAGE_SIZE = 20;

const errorButtonStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: `1px solid ${t.redBorder}`,
  background: 'transparent',
  color: t.redText,
  fontSize: 12.5,
  fontWeight: 600,
  fontFamily: FONT,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const STATUS_TONE: Record<InviteStatus, { bg: string; fg: string; label: string }> = {
  pending: { bg: t.amberBg, fg: t.amberText, label: 'Pending' },
  accepted: { bg: t.accentBg, fg: t.accentText, label: 'Accepted' },
  revoked: { bg: t.surfaceAlt, fg: t.text3, label: 'Revoked' },
  expired: { bg: t.redBg, fg: t.redText, label: 'Expired' },
};

function StatusBadge({ status }: { status: InviteStatus }) {
  const tone = STATUS_TONE[status] ?? STATUS_TONE.pending;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        background: tone.bg,
        color: tone.fg,
        fontSize: 11.5,
        fontWeight: 600,
      }}
    >
      {tone.label}
    </span>
  );
}

interface PendingInvitesListProps {
  /** Called with the invitation id after a successful revoke — lets the parent
   *  clear the create-confirmation token callout if it was for this invite. */
  onRevoked?: (invitationId: string) => void;
}

// SC-2795 — the caller org's pending invitations. The list read is PENDING-only
// and paginated; revoke drops an invite from it. Whole surface gated on
// INVITE_MEMBER by the parent.
export function PendingInvitesList({ onRevoked }: PendingInvitesListProps) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, refetch, isFetching } = useInvitations(page, PAGE_SIZE);
  const revoke = useRevokeInvitation();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // A revoke/accept can shrink the set below the current page (e.g. revoking the
  // only invite on page 2). Reconcile during render — React re-renders with the
  // corrected page before painting — so we never show an empty, unreachable page.
  // Guarded on `data` so an error (no data) doesn't trip it; the error branch owns
  // that recovery.
  if (data && page > totalPages) {
    setPage(totalPages);
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
        <Spinner size={24} />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        role="alert"
        style={{
          padding: '12px 16px',
          background: t.redBg,
          border: `1px solid ${t.red}`,
          borderRadius: 8,
          color: t.redText,
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span style={{ flex: 1 }}>
          Couldn&rsquo;t load pending invites.{' '}
          {error instanceof ApiError ? error.detail : 'Please try again.'}
        </span>
        {/* A later page failed — keep a route back to the known-good first page so
            the user isn't stranded on the failing one with only Retry. */}
        {page > 1 && (
          <button type="button" onClick={() => setPage(1)} style={errorButtonStyle}>
            Back to first page
          </button>
        )}
        {/* A refetch CAN clear a transient/network failure, so a retry is real here. */}
        <button type="button" onClick={() => void refetch()} style={errorButtonStyle}>
          Retry
        </button>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div
        style={{
          padding: '24px 16px',
          textAlign: 'center',
          color: t.text3,
          fontSize: 13,
          border: `1px dashed ${t.border}`,
          borderRadius: 8,
        }}
      >
        No pending invites yet.
      </div>
    );
  }

  const revokeInvite = (id: string) => {
    setConfirmingId(null);
    revoke.mutate(id, { onSuccess: () => onRevoked?.(id) });
  };

  return (
    <section>
      <h2 style={{ fontSize: 15, fontWeight: 600, color: t.text, margin: '0 0 2px' }}>
        Pending invites
      </h2>
      {/* SC-3227 — keeps these visibly apart from the Members roster above. */}
      <div style={{ fontSize: 12.5, color: t.text3, marginBottom: 12 }}>
        Invited, not joined yet.
      </div>

      <div style={{ border: `1px solid ${t.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: t.surfaceAlt }}>
              <Th>Email</Th>
              <Th>Role</Th>
              <Th>Expires</Th>
              <Th>Status</Th>
              <Th style={{ textAlign: 'right' }}>&nbsp;</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((invite) => (
              <InviteRow
                key={invite.id}
                invite={invite}
                confirming={confirmingId === invite.id}
                revoking={revoke.isPending && revoke.variables === invite.id}
                onAskConfirm={() => setConfirmingId(invite.id)}
                onCancelConfirm={() => setConfirmingId(null)}
                onConfirm={() => revokeInvite(invite.id)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {revoke.isError && (
        <div role="alert" style={{ marginTop: 10, fontSize: 12.5, color: t.redText }}>
          Couldn&rsquo;t revoke that invite.{' '}
          {revoke.error instanceof ApiError ? revoke.error.detail : 'Please try again.'}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <PageButton disabled={page <= 1 || isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            ← Prev
          </PageButton>
          <span style={{ fontSize: 12.5, color: t.text3 }}>
            Page {page} of {totalPages}
          </span>
          <PageButton disabled={page >= totalPages || isFetching} onClick={() => setPage((p) => p + 1)}>
            Next →
          </PageButton>
        </div>
      )}
    </section>
  );
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '10px 14px',
        fontSize: 11.5,
        fontWeight: 600,
        color: t.text3,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        ...style,
      }}
    >
      {children}
    </th>
  );
}

interface InviteRowProps {
  invite: Invitation;
  confirming: boolean;
  revoking: boolean;
  onAskConfirm: () => void;
  onCancelConfirm: () => void;
  onConfirm: () => void;
}

function InviteRow({ invite, confirming, revoking, onAskConfirm, onCancelConfirm, onConfirm }: InviteRowProps) {
  return (
    <tr style={{ borderTop: `1px solid ${t.border}` }}>
      <Td style={{ color: t.text, fontWeight: 500 }}>{invite.email}</Td>
      <Td>{ROLE_LABELS[invite.role] ?? invite.role}</Td>
      <Td style={{ color: t.text3 }}>{formatExpiry(invite.expires_at)}</Td>
      <Td>
        <StatusBadge status={invite.status} />
      </Td>
      <Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {confirming ? (
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: t.text3 }}>Revoke?</span>
            <RowButton tone="danger" onClick={onConfirm} disabled={revoking}>
              {revoking ? 'Revoking…' : 'Yes'}
            </RowButton>
            <RowButton tone="neutral" onClick={onCancelConfirm} disabled={revoking}>
              No
            </RowButton>
          </span>
        ) : (
          <RowButton tone="neutral" onClick={onAskConfirm} disabled={revoking}>
            {revoking ? 'Revoking…' : 'Revoke'}
          </RowButton>
        )}
      </Td>
    </tr>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '11px 14px', color: t.text2, ...style }}>{children}</td>;
}

function RowButton({
  children,
  onClick,
  disabled,
  tone,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone: 'danger' | 'neutral';
}) {
  const color = tone === 'danger' ? t.redText : t.text2;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '5px 11px',
        borderRadius: 6,
        border: `1px solid ${t.border2}`,
        background: 'transparent',
        color: disabled ? t.text3 : color,
        fontSize: 12.5,
        fontWeight: 600,
        fontFamily: FONT,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function PageButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 12px',
        borderRadius: 6,
        border: `1px solid ${t.border}`,
        background: 'transparent',
        color: disabled ? t.text3 : t.text,
        fontSize: 12.5,
        fontFamily: FONT,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}
